import { useUserManagementServiceV1 } from "@rw1/api-services";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FC } from "react";
import { Trans } from "react-i18next";
import { ConfirmationDialog } from "../../components/ConfirmationDialog";
import { useLocalization } from "../../hooks/useLocalization";
import { PENDING_INVITATIONS_QUERY_KEY } from "./hooks/usePendinginvitations";

export interface DeleteInvitationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  invitationEmail: string | null;
  companyId?: string;
}

export const DeleteInvitationDialog: FC<DeleteInvitationDialogProps> = ({
  isOpen,
  onClose,
  invitationEmail,
  companyId,
}) => {
  const queryClient = useQueryClient();
  const userManagementService = useUserManagementServiceV1();
  const { t } = useLocalization();

  const deleteInvitationMutation = useMutation({
    mutationFn: async (email: string) => {
      await userManagementService.deleteInvitationByEmail(email);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [PENDING_INVITATIONS_QUERY_KEY, companyId],
      });
      onClose();
    },
  });

  const handleDeleteInvitation = () => {
    if (invitationEmail) {
      deleteInvitationMutation.mutate(invitationEmail);
    }
  };

  const handleCancel = () => {
    onClose();
    deleteInvitationMutation.reset();
  };

  return (
    <ConfirmationDialog.Root onClose={handleCancel} open={isOpen}>
      <ConfirmationDialog.Title>
        {t("users.deleteInvitation")}
      </ConfirmationDialog.Title>
      <ConfirmationDialog.Content error={deleteInvitationMutation.error}>
        <ConfirmationDialog.Text>
          <Trans
            components={{ strong: <strong /> }}
            i18nKey="users.deleteInvitationConfirmation"
            values={{
              email: invitationEmail,
            }}
          />
        </ConfirmationDialog.Text>
      </ConfirmationDialog.Content>
      <ConfirmationDialog.Actions>
        <ConfirmationDialog.Button onClick={handleCancel} variant="contained">
          {t("users.cancel")}
        </ConfirmationDialog.Button>
        <ConfirmationDialog.Button
          isLoading={deleteInvitationMutation.isPending}
          loadingText={t("users.deleting")}
          onClick={handleDeleteInvitation}
          variant="secondary"
        >
          {t("users.delete")}
        </ConfirmationDialog.Button>
      </ConfirmationDialog.Actions>
    </ConfirmationDialog.Root>
  );
};
